/* @flow */
import idx from 'idx';
import * as React from 'react';
import { graphql, compose } from 'react-apollo';
import Modal from 'react-modal';

import { withRouter } from 'app/context/Router';
import ConfirmDialog from 'components/ConfirmDialog';
import CurrentUser from 'auth/CurrentUser';
import DeliveryModal from 'chat_v2/components/DeliveryModal';
import ToastActions from 'actions/ToastActions';
import DELIVERY_SUMMARIES_FROM_STREAM from 'chat/queries/DeliverySummariesFromStreamQuery.gql';
import REQUEST_DELIVERY_FAILURES_MUTATION from 'chat/queries/RequestDeliveryFailuresReportMutation.gql';
import REQUEST_DELIVERY_UNREACHABLES_MUTATION from 'chat/queries/RequestDeliveryUnreachablesReportMutation.gql';
import SETTINGS_ROUTES from 'settings/constants/SettingsRouteConstants';

import type { DeliverySummariesFromStream } from 'chat/queries/__generated__/DeliverySummariesFromStream';
import type {
  fullDeliverySummary,
  DeliveryReportInput,
} from 'chat/fragments/__generated__/fullDeliverySummary';

type StreamDeliverySummary = {
  type: 'stream',
  threadUuid: string,
  messageUuid: string,
};

type AutoMessageSendDeliverySummary = {
  type: 'autoMessageSend',
  readSummary: ?fullDeliverySummary,
  threadUuid: string,
  messageUuid: string,
};

type Props = {
  router: *,
  loading: boolean,
  source: StreamDeliverySummary | AutoMessageSendDeliverySummary,
  onRequestHide: Function,
  data: ReactApolloData<DeliverySummariesFromStream>,
  requestDeliveryFailuresReport: (input: DeliveryReportInput) => Promise<*>,
  requestDeliveryUnreachablesReport: (input: DeliveryReportInput) => Promise<*>,
};

type State = {
  showMissingEmailModal: boolean,
};

class DeliveryModalContainer extends React.Component<Props, State> {
  state = {
    showMissingEmailModal: false,
  };

  componentDidUpdate(prevProps: Props) {
    const prevStreamItem = idx(prevProps, _ => _.data.me.tempStreamItem);
    const streamItem = idx(this.props, _ => _.data.me.tempStreamItem);

    if (!prevStreamItem && streamItem && streamItem.__typename !== 'MessageItem') {
      console.error('delivery modal to fetch delivery for non stream message item');
    }
  }

  getStreamItem = () => idx(this.props, _ => _.data.me.tempStreamItem);

  userHasNoEmail = () => !CurrentUser.hasEmail();

  toggleMissingEmailModal = () =>
    this.setState({ showMissingEmailModal: !this.state.showMissingEmailModal });

  handleDeliveryFailuresRequest = () => {
    if (this.userHasNoEmail()) {
      this.toggleMissingEmailModal();
      return;
    }
    const { threadUuid, messageUuid } = this.props.source;

    this.props
      .requestDeliveryFailuresReport({
        streamUuid: threadUuid,
        messageUuid,
      })
      .then(
        response => {
          const { error } = response.data.requestDeliveryFailuresReport;
          if (error) {
            ToastActions.showErrorToast(error.message);
            return;
          }
          ToastActions.showSuccessToast(
            'You’ve been sent an email with a link to download your CSV'
          );
        },
        error => {
          ToastActions.showDefaultErrorToast();
          console.warn(error);
          console.error('DeliveryModalContainer - failed to request delivery failures');
        }
      );
  };

  handleDeliveryUnreachablesRequest = () => {
    if (this.userHasNoEmail()) {
      this.toggleMissingEmailModal();
      return;
    }
    const { threadUuid, messageUuid } = this.props.source;

    this.props
      .requestDeliveryUnreachablesReport({
        streamUuid: threadUuid,
        messageUuid,
      })
      .then(
        response => {
          const { error } = response.data.requestDeliveryUnreachablesReport;
          if (error) {
            ToastActions.showErrorToast(error.message);
            return;
          }
          ToastActions.showSuccessToast(
            'You’ll receive an email with your CSV when it’s ready to download'
          );
        },
        error => {
          ToastActions.showDefaultErrorToast();
          console.warn(error);
          console.error('DeliveryModalContainer - failed to request delivery unreachables');
        }
      );
  };

  renderMissingEmailModal = () => {
    return (
      <Modal isOpen={this.state.showMissingEmailModal}>
        <ConfirmDialog
          title="Add an email address to your account first"
          subtext="CSV downloads are sent to your email. You can add an email address in your account settings."
          actionButtonText="Go to Settings"
          cancelButtonText="Cancel"
          onConfirm={() => this.props.router.push(SETTINGS_ROUTES.NOTIFICATIONS)}
          onCancel={this.toggleMissingEmailModal}
          onRequestHide={this.toggleMissingEmailModal}
          confirmEventKey="delivery_summary.no_email_modal.go_to_settings.click"
          cancelEventKey="delivery_summary.no_email_modal.cancel.click"
          eventKey="delivery_summary.no_email_modal.view"
        />
      </Modal>
    );
  };

  render() {
    const { data, onRequestHide, source } = this.props;

    if (source.type === 'autoMessageSend') {
      return (
        <React.Fragment>
          {this.renderMissingEmailModal()}
          <DeliveryModal
            loading={false}
            onRequestHide={onRequestHide}
            messageReadSummaries={source.readSummary}
            onRequestDeliveryFailures={this.handleDeliveryFailuresRequest}
            onRequestDeliveryUnreachables={this.handleDeliveryUnreachablesRequest}
          />
        </React.Fragment>
      );
    }

    const streamItem = this.getStreamItem();

    if (!streamItem) {
      return <DeliveryModal loading={data.loading} onRequestHide={onRequestHide} />;
    }

    if (streamItem.__typename !== 'MessageItem') {
      return null;
    }

    return (
      <React.Fragment>
        {this.renderMissingEmailModal()}
        <DeliveryModal
          loading={data.loading}
          onRequestHide={onRequestHide}
          messageReadSummaries={streamItem.readSummary}
          onRefetch={() => data.refetch()}
          onRequestDeliveryFailures={this.handleDeliveryFailuresRequest}
          onRequestDeliveryUnreachables={this.handleDeliveryUnreachablesRequest}
        />
      </React.Fragment>
    );
  }
}

const withDeliverySummaryFromStream = graphql(DELIVERY_SUMMARIES_FROM_STREAM, {
  skip: ({ source }) => source.type !== 'stream',
  options: ({ source: { messageUuid, threadUuid } }) => ({
    fetchPolicy: 'network-only',
    errorPolicy: 'all',
    variables: {
      itemUuid: messageUuid,
      threadUuid,
    },
  }),
});

const withDeliveryFailuresReportMutation = graphql(REQUEST_DELIVERY_FAILURES_MUTATION, {
  props({ mutate }) {
    return {
      requestDeliveryFailuresReport(input: DeliveryReportInput) {
        return mutate({
          variables: { input },
        });
      },
    };
  },
});

const withDeliveryUnreachablesReportMutation = graphql(REQUEST_DELIVERY_UNREACHABLES_MUTATION, {
  props({ mutate }) {
    return {
      requestDeliveryUnreachablesReport(input: DeliveryReportInput) {
        return mutate({
          variables: { input },
        });
      },
    };
  },
});

export { DeliveryModalContainer };
export default compose(
  withRouter,
  withDeliverySummaryFromStream,
  withDeliveryFailuresReportMutation,
  withDeliveryUnreachablesReportMutation
)(DeliveryModalContainer);
